/** @jsx jsx */
import { memo } from 'react';
import { styled, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { getColorMetadata } from '../../../utils/colors/index.tsx';

export type Props = {
	categoryName: string;
	categoryColor: string | null;
	isInteractive?: boolean;
};

const CategoryTag = ({ categoryName, categoryColor, isInteractive = true }: Props): JSX.Element => {
	const { backgroundColor: tagColor } = getColorMetadata(categoryColor);

	return (
		<CategoryTagWrapper
			isInteractive={isInteractive}
			textColor={token('color.text')}
			aria-label={categoryName}
			data-testid="business-categories.ui.category-form.category-tag.category-tag-wrapper"
		>
			<StyledTag color={tagColor}>{categoryName}</StyledTag>
		</CategoryTagWrapper>
	);
};

export default memo(CategoryTag);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CategoryTagWrapper = styled.div<{ textColor: string; isInteractive: boolean }>({
	display: 'inline-flex',
	justifyContent: 'left',
	alignItems: 'center',
	transition: '0.3s',
	maxWidth: '100%',
	minWidth: 0,
	minHeight: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ textColor }) => textColor,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		cursor: ({ isInteractive }) => (isInteractive ? 'pointer' : 'default'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTag = styled.div<{ color: string }>({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "2px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.xsmall', '3px'),
	backgroundColor: 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	border: ({ color }) => `1px solid ${color}`,
	paddingTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	maxWidth: '100%',
});
